<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import {
  inventoryApi, type InventoryBalance, type InventoryUnit, type PlaceFilter, type UnitCondition,
} from '@/api/inventory';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { CONDITIONS, categoryColor, formatQuantity } from '@/utils/inventory';
import ListToolbar from '@/components/list-toolbar.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import InventoryConditionDialog from './inventory-condition-dialog.vue';
import InventoryMovementDialog, { type MovementMode } from './inventory-movement-dialog.vue';
import InventoryMinimumDialog from './inventory-minimum-dialog.vue';
import InventoryMovementHistory from './inventory-movement-history.vue';

// Items of one place (a patient's home or a warehouse): supplies and equipment
// in one table with search and a category filter, plus the place's history.
// Each place passes its own actions; the `primary` one is a button on the row.
export interface PlaceAction { mode: MovementMode; title: string; primary?: boolean }

const props = withDefaults(defineProps<{
  place: PlaceFilter;
  balanceActions: PlaceAction[];
  unitActions: PlaceAction[];
  canEnter?: boolean;
  emptyText?: string;
}>(), { canEnter: true, emptyText: 'Sin artículos' });

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();
const canManage = computed(() => hasPermission.value('inventory', 'manage'));
const place = computed(() => props.place);

const { data: balances, isPending: loadingBalances } = useQuery({
  queryKey: ['inventoryBalances', place],
  queryFn: () => inventoryApi.balances(place.value),
  initialData: [],
});

const { data: units } = useQuery({
  queryKey: ['inventoryUnits', place],
  queryFn: () => inventoryApi.units({ ...place.value, status: 'in_service' }),
  initialData: [],
});

const dialog = ref<{ mode: MovementMode; balance?: InventoryBalance; unit?: InventoryUnit } | null>(null);
const minimumFor = ref<InventoryBalance | null>(null);
const conditionFor = ref<InventoryUnit | null>(null);

function open(mode: MovementMode, target: { balance?: InventoryBalance; unit?: InventoryUnit } = {}) {
  dialog.value = { mode, ...target };
}

function conditionColor(condition: UnitCondition): string {
  return CONDITIONS.find((option) => option.value === condition)?.color ?? 'default';
}

// Supplies (stock per item) and equipment (one row per unit) in one table.
interface ItemRow {
  key: string;
  itemName: string;
  inventoryCategoryId: number;
  categoryName: string;
  identifier?: string;
  balance?: InventoryBalance;
  unit?: InventoryUnit;
}

const rows = computed<ItemRow[]>(() => [
  ...balances.value
    .filter((balance) => balance.tracking === 'quantity')
    .map((balance) => ({
      key: `balance-${balance.id}`, itemName: balance.itemName, inventoryCategoryId: balance.inventoryCategoryId,
      categoryName: balance.categoryName, balance,
    })),
  ...units.value.map((unit) => ({
    key: `unit-${unit.id}`, itemName: unit.itemName, inventoryCategoryId: unit.inventoryCategoryId,
    categoryName: unit.categoryName, identifier: unit.identifier ?? undefined, unit,
  })),
].sort((a, b) => a.itemName.localeCompare(b.itemName)));

const categories = computed(() => {
  const byId = new Map(rows.value.map((row) => [row.inventoryCategoryId, row.categoryName]));

  return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
});
const categoryIds = ref<number[]>([]);
const search = ref('');
// Category filter plus a free-text search over name, category and equipment identifier.
const filteredRows = computed(() => {
  const query = (search.value ?? '').trim().toLocaleLowerCase();

  return rows.value.filter((row) => (
    (categoryIds.value.length === 0 || categoryIds.value.includes(row.inventoryCategoryId)) &&
    (!query || [row.itemName, row.categoryName, row.identifier]
      .some((text) => text?.toLocaleLowerCase().includes(query)))
  ));
});

const tab = ref<'items' | 'history'>('items');

const headers = computed(() => [
  { title: 'Artículo', key: 'itemName' },
  { title: 'Categoría', key: 'categoryName' },
  { title: 'Stock', key: 'stock', sortable: false, align: 'end' as const },
  { title: 'Mínimo', key: 'minimum', sortable: false, align: 'end' as const },
  { title: 'Condición', key: 'condition', sortable: false },
  ...(canManage.value ? [{ title: '', key: 'actions', sortable: false, align: 'end' as const }] : []),
]);

function actionsFor(row: ItemRow): PlaceAction[] {
  return row.unit ? props.unitActions : props.balanceActions;
}

function primaryAction(row: ItemRow): PlaceAction | undefined {
  return actionsFor(row).find((action) => action.primary);
}

function menuActions(row: ItemRow): PlaceAction[] {
  return actionsFor(row).filter((action) => !action.primary);
}

function openFor(row: ItemRow, mode: MovementMode) {
  open(mode, row.unit ? { unit: row.unit } : { balance: row.balance });
}
</script>
<template>
  <div class="d-flex flex-column ga-4">
    <div class="d-flex flex-wrap align-center ga-2">
      <v-tabs
        v-model="tab"
        color="primary"
      >
        <v-tab
          value="items"
          prepend-icon="mdi-package-variant-closed"
        >
          Artículos
        </v-tab>
        <v-tab
          value="history"
          prepend-icon="mdi-history"
        >
          Historial
        </v-tab>
      </v-tabs>
      <v-spacer />
      <v-btn
        v-if="canManage && canEnter"
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        @click="open('entry')"
      >
        Registrar ingreso
      </v-btn>
    </div>

    <v-window v-model="tab">
      <v-window-item value="items">
        <list-toolbar
          v-model:search="search"
          search-label="Buscar artículo"
          :active-filter-count="categoryIds.length ? 1 : 0"
        >
          <template #filters>
            <v-select
              v-model="categoryIds"
              :items="categories"
              item-title="name"
              item-value="id"
              label="Categoría"
              variant="underlined"
              multiple
              chips
              closable-chips
              clearable
              hide-details
              min-width="220"
            />
          </template>
        </list-toolbar>
        <v-data-table
          v-bind="tableProps"
          :header-props="{ class: 'font-weight-bold' }"
          :headers="headers"
          :items="filteredRows"
          item-value="key"
          :loading="loadingBalances"
          :no-data-text="emptyText"
          density="comfortable"
        >
          <template #[`item.categoryName`]="{ item }">
            <v-chip
              :color="categoryColor(item.inventoryCategoryId)"
              size="small"
              variant="tonal"
            >
              {{ item.categoryName }}
            </v-chip>
          </template>
          <template #[`item.itemName`]="{ item }">
            {{ item.itemName }}
            <v-chip
              v-if="item.balance?.lowStock"
              color="warning"
              size="small"
              class="ml-2"
            >
              Stock bajo
            </v-chip>
            <div
              v-if="item.unit"
              class="text-caption text-medium-emphasis"
            >
              {{ item.unit.identifier || 'Sin identificador' }} · Dueño: {{ item.unit.ownerName || 'la empresa' }}
            </div>
          </template>
          <template #[`item.stock`]="{ item }">
            <template v-if="item.balance">
              {{ formatQuantity(item.balance.quantity) }} {{ item.balance.humanUnitOfMeasure.toLowerCase() }}
            </template>
            <template v-else>
              1 equipo
            </template>
          </template>
          <template #[`item.minimum`]="{ item }">
            <template v-if="item.balance">
              <v-btn
                v-if="canManage"
                size="small"
                variant="text"
                :color="item.balance.minQuantity === null ? 'primary' : undefined"
                :prepend-icon="item.balance.minQuantity === null ? 'mdi-bell-plus' : undefined"
                @click="minimumFor = item.balance"
              >
                {{ item.balance.minQuantity === null ? 'Definir' : formatQuantity(item.balance.minQuantity) }}
              </v-btn>
              <template v-else>
                {{ formatQuantity(item.balance.minQuantity) }}
              </template>
            </template>
            <span
              v-else
              class="text-medium-emphasis"
            >—</span>
          </template>
          <template #[`item.condition`]="{ item }">
            <v-chip
              v-if="item.unit"
              :color="conditionColor(item.unit.condition)"
              size="small"
            >
              {{ item.unit.humanCondition }}
            </v-chip>
            <span
              v-else
              class="text-medium-emphasis"
            >—</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="primaryAction(item)"
              size="small"
              variant="text"
              color="primary"
              @click="openFor(item, primaryAction(item)!.mode)"
            >
              {{ primaryAction(item)!.title }}
            </v-btn>
            <v-menu>
              <template #activator="{ props: menuProps }">
                <v-btn
                  v-bind="menuProps"
                  icon="mdi-dots-vertical"
                  size="small"
                  variant="text"
                  aria-label="Más acciones"
                />
              </template>
              <v-list density="compact">
                <v-list-item
                  v-if="item.unit"
                  title="Actualizar condición"
                  @click="conditionFor = item.unit"
                />
                <v-list-item
                  v-for="action in menuActions(item)"
                  :key="action.mode"
                  :title="action.title"
                  @click="openFor(item, action.mode)"
                />
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
      </v-window-item>

      <v-window-item value="history">
        <inventory-movement-history :place="place" />
      </v-window-item>
    </v-window>

    <responsive-dialog
      :model-value="!!dialog"
      max-width="560"
      scrollable
      @update:model-value="dialog = null"
    >
      <inventory-movement-dialog
        v-if="dialog"
        :place="place"
        :mode="dialog.mode"
        :balance="dialog.balance"
        :unit="dialog.unit"
        @close="dialog = null"
      />
    </responsive-dialog>
    <responsive-dialog
      :model-value="!!conditionFor"
      max-width="420"
      @update:model-value="conditionFor = null"
    >
      <inventory-condition-dialog
        v-if="conditionFor"
        :unit="conditionFor"
        @close="conditionFor = null"
      />
    </responsive-dialog>
    <responsive-dialog
      :model-value="!!minimumFor"
      max-width="420"
      @update:model-value="minimumFor = null"
    >
      <inventory-minimum-dialog
        v-if="minimumFor"
        :place="place"
        :balance="minimumFor"
        @close="minimumFor = null"
      />
    </responsive-dialog>
  </div>
</template>
